import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
	Autocomplete,
	TextField,
	Paper,
	Box,
	Typography,
	CircularProgress,
	Alert,
	InputAdornment
} from '@mui/material';
import { Search as SearchIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { searchOnHomesPublic } from '../../../api/serverRequests';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: '100vh',
		padding: theme.spacing(3),
		backgroundColor: '#f5f5f5'
	},
	rootIframe: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'flex-start',
		minHeight: 'auto',
		padding: theme.spacing(1),
		margin: 0,
		backgroundColor: 'transparent',
		overflow: 'hidden',
		boxSizing: 'border-box',
		width: '100%'
	},
	container: {
		width: '100%',
		maxWidth: '600px',
		padding: 0,
		backgroundColor: 'white',
		borderRadius: 16,
		boxShadow: '0 8px 32px rgba(0, 79, 43, 0.12)',
		overflow: 'hidden'
	},
	containerIframe: {
		width: '100%',
		maxWidth: '600px',
		padding: 0,
		backgroundColor: 'white',
		borderRadius: 12,
		boxShadow: '0 4px 20px rgba(0, 79, 43, 0.1)',
		overflow: 'hidden'
	},
	header: {
		background: 'linear-gradient(135deg, #004F2B 0%, #007A42 100%)',
		padding: theme.spacing(3, 4),
		textAlign: 'center'
	},
	headerIframe: {
		background: 'linear-gradient(135deg, #004F2B 0%, #007A42 100%)',
		padding: theme.spacing(1.5, 2),
		textAlign: 'center'
	},
	title: {
		color: '#fff',
		fontWeight: 700,
		letterSpacing: '0.02em'
	},
	titleIframe: {
		color: '#fff',
		fontWeight: 700,
		fontSize: '1.1rem',
		letterSpacing: '0.02em'
	},
	subtitle: {
		color: 'rgba(255,255,255,0.8)',
		fontSize: '0.85rem',
		marginTop: 4
	},
	subtitleIframe: {
		color: 'rgba(255,255,255,0.8)',
		fontSize: '0.75rem',
		marginTop: 2
	},
	body: {
		padding: theme.spacing(3, 4, 2)
	},
	bodyIframe: {
		padding: theme.spacing(2, 2, 1.5)
	},
	searchBox: {
		width: '100%'
	}
}));

export default function Widget() {
	const classes = useStyles();
	const location = useLocation();
	const [searchInput, setSearchInput] = useState('');
	const [autocompleteOpen, setAutocompleteOpen] = useState(false);
	const [addressOptions, setAddressOptions] = useState([]);
	const [addressLoading, setAddressLoading] = useState(false);
	const [userId, setUserId] = useState(null);
	const [orgId, setOrgId] = useState(null);
	const [error, setError] = useState(null);
	const searchTimeoutRef = useRef(null);
	const isIframe = window.self !== window.top;

	// Get user/org ID from URL parameters
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const urlUserId = params.get('userId');
		const urlOrgId = params.get('orgId');

		if (urlUserId && urlOrgId) {
			setUserId(urlUserId);
			setOrgId(urlOrgId);
		} else if (isIframe) {
			// If in iframe without params, try to get from parent via postMessage
			window.addEventListener('message', handleParentMessage);
			// Request config from parent
			window.parent.postMessage({ type: 'WIDGET_REQUEST_CONFIG' }, '*');

			return () => {
				window.removeEventListener('message', handleParentMessage);
			};
		}
	}, [location.search, isIframe]);

	const handleParentMessage = (event) => {
		// In production, verify event.origin for security
		if (event.data && event.data.type === 'WIDGET_CONFIG') {
			if (event.data.userId && event.data.orgId) {
				setUserId(event.data.userId);
				setOrgId(event.data.orgId);
			}
		}
	};

	// Handle address search input with debounce
	const handleAddressInputChange = (event, newInputValue, reason) => {
		setSearchInput(newInputValue);
		setError(null);

		// Clear previous timeout
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		// Only search when user is typing and has at least 3 characters
		if (reason === 'input' && newInputValue.trim() && newInputValue.trim().length >= 3) {
			// Check if we have required IDs (only needed if in iframe mode)
			if (isIframe && (!userId || !orgId)) {
				setError('Widget configuration missing. Please provide userId and orgId.');
				return;
			}

			setAddressLoading(true);
			searchTimeoutRef.current = setTimeout(() => {
				// Pass null/false to search all homes regardless of geospatial data
				// Include userId and orgId if available
				searchOnHomesPublic(newInputValue, false, true)
					.then((res) => {
						console.log('Search response:', res.data);
						if (res.data.success) {
							// Handle the homes array from the response
							const homes = res.data.homes || [];
							console.log('Homes found:', homes.length, homes);
							setAddressOptions(homes);
						} else {
							console.log('Search unsuccessful');
							setAddressOptions([]);
						}
					})
					.catch((err) => {
						console.error('Address search failed:', err);
						setAddressOptions([]);
						setError('Search failed. Please try again.');
					})
					.finally(() => {
						setAddressLoading(false);
					});
			}, 300); // 300ms debounce (faster response)
		} else if (!newInputValue.trim() || newInputValue.trim().length < 3) {
			setAddressOptions([]);
			setAddressLoading(false);
		}
	};

	// Handle address selection - navigate to lead capture page
	const handleAddressChange = (event, newValue, reason) => {
		if (reason === 'selectOption' && newValue) {
			console.log('Selected home:', newValue);

			// Build data object with selected home information
			const homeData = {
				address: newValue.address || newValue.Address || '',
				city: newValue.city || newValue.City || '',
				homeId: newValue.id || newValue.Id || newValue.HomeId || ''
			};

			// Build query parameters for lead capture page
			const params = new URLSearchParams();
			if (homeData.address) params.append('address', homeData.address);
			if (homeData.city) params.append('city', homeData.city);
			if (homeData.homeId) params.append('homeId', homeData.homeId);
			if (userId) params.append('userId', userId);
			if (orgId) params.append('orgId', orgId);

			const baseUrl = isIframe ? window.location.origin : '';
			const leadCaptureUrl = `${baseUrl}/leadcapture?${params.toString()}`;

			if (isIframe) {
				// If in iframe, try to navigate parent window or open in new tab
				try {
					// Try to navigate the parent window (works if same-origin)
					window.top.location.href = leadCaptureUrl;
				} catch (e) {
					// Cross-origin restriction - open in new window/tab instead
					// Also send postMessage for parent pages that want to handle it
					window.parent.postMessage(
						{
							type: 'WIDGET_HOME_SELECTED',
							data: homeData
						},
						'*'
					);

					// Open lead capture in new tab
					window.open(leadCaptureUrl, '_blank');
				}
			} else {
				// If not in iframe, navigate normally
				window.location.href = leadCaptureUrl;
			}
		}
	};

	// Format the display label for each option
	const getOptionLabel = (option) => {
		// Check if option is just a string (shouldn't happen, but just in case)
		if (typeof option === 'string') {
			return option;
		}

		// If option has a pre-formatted 'value' field, use it
		if (option.value) {
			return option.value;
		}

		// Otherwise, construct from address and city
		const addressPart = option.address || option.Address || '';
		const cityPart = option.city || option.City || '';

		if (addressPart && cityPart) {
			return `${addressPart}, ${cityPart}`;
		} else if (addressPart) {
			return addressPart;
		} else if (cityPart) {
			return cityPart;
		}

		// Fallback - return empty string if nothing found
		return 'Unknown Address';
	};

	// Show error if in iframe mode without required IDs
	if (isIframe && (!userId || !orgId)) {
		return (
			<Box className={classes.rootIframe}>
				<Paper className={classes.containerIframe} elevation={2}>
					<Alert severity="warning">
						Widget configuration required. Please provide userId and orgId as URL
						parameters or via postMessage.
					</Alert>
				</Paper>
			</Box>
		);
	}

	return (
		<Box className={isIframe ? classes.rootIframe : classes.root}>
			<Paper className={isIframe ? classes.containerIframe : classes.container} elevation={0}>
				<Box className={isIframe ? classes.headerIframe : classes.header}>
					<Typography
						variant={isIframe ? 'h6' : 'h5'}
						className={isIframe ? classes.titleIframe : classes.title}
					>
						What's Happening Near You
					</Typography>
					<Typography className={isIframe ? classes.subtitleIframe : classes.subtitle}>
						See what's selling, what's sold, and what's happening around any address
					</Typography>
				</Box>
				<Box className={isIframe ? classes.bodyIframe : classes.body}>
					{error && (
						<Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
							{error}
						</Alert>
					)}
					<Box className={classes.searchBox}>
						<Autocomplete
							id="widget-address-search"
							open={searchInput !== '' && autocompleteOpen}
							onOpen={() => setAutocompleteOpen(true)}
							onClose={() => setAutocompleteOpen(false)}
							inputValue={searchInput}
							onInputChange={handleAddressInputChange}
							onChange={handleAddressChange}
							options={addressOptions}
							loading={addressLoading}
							clearOnBlur={false}
							clearOnEscape={true}
							isOptionEqualToValue={(option, value) => {
								const optionId = option.HomeId || option.Id || option.id;
								const valueId = value.HomeId || value.Id || value.id;
								return optionId === valueId;
							}}
							getOptionLabel={getOptionLabel}
							PaperComponent={(props) => (
								<Paper
									elevation={4}
									{...props}
									sx={{
										borderRadius: 2,
										mt: 0.5,
										'& .MuiAutocomplete-listbox': {
											padding: '4px 0'
										}
									}}
								/>
							)}
							renderOption={(props, option, { inputValue }) => {
								const displayValue = getOptionLabel(option);
								const parts = displayValue.split(
									new RegExp(
										`(${inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
										'gi'
									)
								);

								return (
									<li
										{...props}
										style={{
											...props.style,
											padding: '10px 16px',
											borderBottom: '1px solid #f0f0f0'
										}}
									>
										<LocationIcon
											sx={{
												color: '#004F2B',
												fontSize: 18,
												mr: 1.5,
												flexShrink: 0
											}}
										/>
										<span>
											{parts.map((part, index) => (
												<span
													key={index}
													style={{
														fontWeight:
															part.toLowerCase() ===
															inputValue.toLowerCase()
																? 700
																: 400,
														color:
															part.toLowerCase() ===
															inputValue.toLowerCase()
																? '#004F2B'
																: 'inherit'
													}}
												>
													{part}
												</span>
											))}
										</span>
									</li>
								);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									placeholder="Enter an address..."
									variant="outlined"
									fullWidth
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: 3,
											backgroundColor: '#f8faf9',
											transition: 'all 0.2s ease',
											'&:hover': {
												backgroundColor: '#fff',
												boxShadow: '0 2px 8px rgba(0,79,43,0.08)'
											},
											'&.Mui-focused': {
												backgroundColor: '#fff',
												boxShadow: '0 2px 12px rgba(0,79,43,0.12)'
											}
										}
									}}
									InputProps={{
										...params.InputProps,
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon
													sx={{ color: '#004F2B', opacity: 0.6 }}
												/>
											</InputAdornment>
										),
										endAdornment: (
											<>
												{addressLoading ? (
													<CircularProgress
														size={20}
														sx={{ color: '#004F2B' }}
													/>
												) : null}
												{params.InputProps.endAdornment}
											</>
										)
									}}
								/>
							)}
						/>
					</Box>
					<a
						href="https://www.realsaige.com"
						target="_blank"
						rel="noopener noreferrer"
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '4px',
							marginTop: 8,
							opacity: 0.5,
							textDecoration: 'none',
							padding: '0 16px 12px'
						}}
					>
						<Typography sx={{ fontSize: '0.7rem', color: '#999', lineHeight: 1 }}>
							Powered by
						</Typography>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="170 430 780 210"
							style={{ height: 16, width: 'auto' }}
						>
							<path
								fill="#004f2b"
								d="m439.95,481.28c-1.59-10.52-5.96-14.3-13.31-14.3-6.95,0-12.91,4.96-12.91,11.52,0,8.74,7.94,12.71,24.03,19.66,32.37,13.9,40.51,25.62,40.51,44.88,0,28.4-19.46,45.08-51.24,45.08s-53.02-16.68-53.02-48.26v-3.18h37.33c.2,12.11,6.16,20.06,15.49,20.06,7.74,0,14.1-6.16,14.1-14.1,0-11.72-16.28-16.88-29.59-22.24-24.03-9.73-34.95-22.44-34.95-41.51,0-25.02,24.23-43.29,51.24-43.29,9.73,0,18.27,2.18,26.02,6.16,15.69,8.14,23.23,19.86,23.43,39.52h-37.14Z"
							/>
							<path
								fill="#f90"
								d="m580.55,585.34v-11.92h-.4c-4.77,9.73-17.87,15.69-32.37,15.69-32.57,0-55.21-25.62-55.21-58.98s23.63-58.78,55.21-58.78c13.5,0,26.02,5.36,32.37,15.49h.4v-11.72h32.97v110.22h-32.97Zm0-55.21c0-15.49-12.51-28-28-28s-27.01,12.51-27.01,28.4,12.11,27.8,27.4,27.8,27.6-12.31,27.6-28.2Z"
							/>
							<path
								fill="#f90"
								d="m637.55,463.41v-25.02h32.97v25.02h-32.97Zm0,121.93v-110.22h32.97v110.22h-32.97Z"
							/>
							<path
								fill="#004f2b"
								d="m775.37,572.43c-4.96,10.52-17.28,16.48-30.78,16.48-32.17,0-55.41-24.82-55.41-57.99s23.63-59.38,55.41-59.38c14.3,0,25.22,5.36,30.58,13.9h.4v-10.33h32.97v98.5c0,14.89-1.99,26.21-5.96,33.76-7.94,15.29-29.59,27.01-57.19,27.01-31.18,0-51.43-17.67-54.21-38.72h38.13c3.77,8.14,11.12,10.92,19.26,10.92,16.68,0,26.81-10.92,26.81-30.98v-3.18Zm.2-41.7c0-16.09-10.92-28.4-27.01-28.4-14.7,0-26.41,12.31-26.41,27.4s10.52,28.4,26.41,28.4,27.01-11.52,27.01-27.4Z"
							/>
							<path
								fill="#004f2b"
								d="m858.58,540.26c1.79,12.51,12.31,20.85,25.82,20.85,8.94,0,14.7-3.38,20.06-10.33h33.76c-10.52,24.03-30.58,38.13-53.82,38.13-31.77,0-58.39-25.62-58.39-58.39s25.42-59.18,57.79-59.18,58.19,26.02,58.19,60.17c0,3.18-.2,5.76-.79,8.74h-82.61Zm50.44-22.24c-2.58-11.32-12.51-18.87-25.22-18.87-11.92,0-22.24,7.15-24.82,18.87h50.04Z"
							/>
							<path
								fill="#f90"
								d="m181.78,518.01c-.03,38.68,18.29,70.17,40.84,70.18,11.94,0,25.89-14.98,35.73-26.94.37-.44.73-.89,1.09-1.33,3.53-4.33,6.81-8.65,9.6-12.48,10.27-14.02,19.4-30.55,30.44-42.82,6-6.66,13.55-16.02,20.99-11.76,6.28,3.6,10.64,13.99,10.64,25.36-.01,14.73-7.01,26.71-15.59,26.7-.05,0-4.43-.17-16.14-13.24l-12.5,19.17c-.5.76-3.15,4.8-7.16,10.42,9.82,11.99,23.81,27.09,35.78,27.1,22.55.02,40.91-31.44,40.94-70.12.03-38.68-18.29-70.17-40.84-70.18-11.94,0-25.9,14.98-35.73,26.94-.37.44-.73.89-1.09,1.33-3.53,4.33-6.81,8.66-9.61,12.48-3.55,4.85-6.32,8.89-7.86,11.19-.79,1.17-1.26,1.88-1.33,2-4.93,7.73-9.93,16.08-15.44,22.84-1.23,1.51-2.4,2.91-3.52,4.2-.79.92-1.55,1.78-2.29,2.6-3.73,4.14-7.62,8.16-11.76,10.99-9.72,6.66-19.87-7.32-19.86-24.6.01-14.73,7-26.71,15.59-26.7.05,0,3.81.15,13.71,10.6.77.81,1.58,1.69,2.43,2.64l12.51-19.17s.01-.01.02-.03c.54-.82,3.18-4.84,7.14-10.39-9.82-11.98-23.81-27.08-35.78-27.09-22.55-.02-40.91,31.44-40.94,70.12Z"
							/>
						</svg>
					</a>
				</Box>
			</Paper>
		</Box>
	);
}
